/**
 * OV2 order-entry catalog — cached 30 min so reopening New Order is instant.
 */
import { useQuery } from "@tanstack/react-query";
import api, {
  getSocieties,
  getOccasions,
  getWeightOptions,
  getDeliverySlots,
  getFlavors,
  getDryCakes,
  getCheesecakes,
  getPackagingOptions,
} from "@/lib/api";

export const ORDER_CATALOG_KEY = "order-catalog";

const DEFAULT_OCCASIONS = [
  "Birthday",
  "Anniversary",
  "Baby Shower",
  "Engagement",
  "Wedding",
  "Corporate",
  "Other",
];

function mergeOccasions(list) {
  const base = Array.isArray(list) ? list.filter(Boolean) : [];
  return Array.from(new Set([...base, ...DEFAULT_OCCASIONS]));
}

export async function fetchOrderCatalog() {
  const [
    societiesRes,
    occasionsRes,
    weightsRes,
    slotsRes,
    flavorsRes,
    dryCakesRes,
    cheesecakesRes,
    packagingRes,
    boxTypesRes,
  ] = await Promise.all([
    getSocieties().catch(() => ({ data: { societies: [] } })),
    getOccasions().catch(() => ({ data: { occasions: DEFAULT_OCCASIONS } })),
    getWeightOptions().catch(() => ({ data: { weights: [] } })),
    getDeliverySlots().catch(() => ({ data: { slots: [] } })),
    getFlavors().catch(() => ({ data: { flavors: [] } })),
    getDryCakes().catch(() => ({ data: { dry_cakes: [] } })),
    getCheesecakes().catch(() => ({ data: { cheesecakes: [] } })),
    getPackagingOptions().catch(() => ({ data: { packaging_options: [] } })),
    api.get("/box-types").catch(() => ({ data: { box_types: [] } })),
  ]);

  return {
    societies: societiesRes.data.societies || [],
    occasions: mergeOccasions(occasionsRes?.data?.occasions || DEFAULT_OCCASIONS),
    weights: weightsRes.data.weights || [],
    slots: slotsRes.data.slots || [],
    flavors: flavorsRes.data.flavors || [],
    dry_cakes: dryCakesRes.data.dry_cakes || [],
    cheesecakes: cheesecakesRes.data.cheesecakes || [],
    packaging_options: packagingRes.data.packaging_options || [],
    box_types: boxTypesRes.data.box_types || [],
  };
}

export function useOrderCatalog() {
  return useQuery({
    queryKey: [ORDER_CATALOG_KEY],
    queryFn: fetchOrderCatalog,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function getDefaultCakeItemFromCatalog(flavors) {
  const list = flavors || [];
  const first = list[0]?.flavor || list[0]?.name || (typeof list[0] === "string" ? list[0] : "") || "Chocolate Truffle";
  return {
    flavor: first,
    weight_kg: 0.5,
    quantity: 1,
    order_type: "Custom",
    base_flour: "Maida",
    sweetener: "Sugar",
    base_fat: "Oil",
    box_type: "Auto",
    decor_level: "Standard",
    tiers: 0,
    packaging_type: "half_kg",
    message_on_cake: "",
  };
}
